import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, SAMPLES_BASE, Scene } from "./types";

// Lesson: Reports from audio → Dictate additions. Fixture `dictateAdditions`:
// the Valley Mart report with its arrival time still a placeholder. The officer
// uploads audio through Address Suggestions, reviews three changes (accept,
// accept, discard), then opens the Transcribe Audio menu to show the two
// dictation modes (no microphone is used in the stage).
const CASE_ID = "c0000000-0000-4000-8000-000000000211";
const UPLOAD_AUDIO = `${sel("addToReportButton")} ul li:nth-child(2) button`;
const LAST_PARAGRAPH = `${sel("editorNarrative")} p:last-of-type`;
const REVIEW_AT = (n: number) => `${sel("suggestionsReview")}[data-review-index="${n}"]`;

const scene: Scene = {
  id: "dictate-additions",
  title: "Dictate additions",
  startRoute: `/cases/${CASE_ID}`,
  fixture: "dictateAdditions",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "You rarely get everything into the first recording. Instead of typing the rest, just talk. There are two ways to add by voice: answer the open suggestions with a recording, or dictate straight into the editor at your cursor.",
      actions: [
        { type: "waitFor", target: sel("reportScore") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "address",
      narration:
        "Let's start with the suggestions. This report is still missing the arrival time, and one other detail. I'll click Address Suggestions and choose Upload Audio. Record Audio works the same way, with your microphone.",
      actions: [
        // "I'll click Address Suggestions" at 6.3 s, "choose Upload Audio" at 8.7 s.
        { type: "moveTo", target: sel("suggestionItem") },
        { type: "spotlight", target: sel("suggestionItem") },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: null },
        { type: "moveTo", target: `${sel("addToReportButton")} button` },
        { type: "wait", ms: 200 },
        { type: "click", target: `${sel("addToReportButton")} button` },
        { type: "wait", ms: 1300 },
        { type: "click", target: UPLOAD_AUDIO },
        { type: "waitFor", target: sel("uploadDropzone") },
      ],
    },
    {
      id: "upload",
      narration: "The window lists what's still open, so you know what to speak to. I'll add my recording and upload it.",
      actions: [
        // "I'll add my recording" at 3.7 s.
        { type: "moveTo", target: sel("uploadDropzone") },
        { type: "wait", ms: 2600 },
        {
          type: "attachFile",
          // Two upload modals exist in the page (sidebar and Suggestions); take the open one.
          target: `dialog[open] ${sel("uploadDropzone")} input[type="file"]`,
          url: `${SAMPLES_BASE}/narrative-retail-theft.mp3`,
          name: "suggestion-answers.mp3",
          mime: "audio/mpeg",
        },
        { type: "waitFor", target: sel("uploadFileRow") },
        { type: "wait", ms: 1000 },
        { type: "click", target: sel("uploadSubmit") },
      ],
    },
    {
      id: "generating",
      narration:
        "Now the AI works your answers into the report. You can keep working while it does, or leave the page and come back.",
      actions: [{ type: "waitFor", target: REVIEW_AT(0), timeoutMs: 25000 }],
    },
    {
      id: "review",
      narration:
        "When it's ready, a review card walks you through every change, one at a time. This first one fills in the arrival time. It's right, so I'll accept it. The next one adds which way the subject left. I'll accept that too.",
      actions: [
        // Accept #1 at about 8.3 s ("so I'll accept it"), accept #2 at about 12.3 s.
        { type: "waitFor", target: REVIEW_AT(0) },
        { type: "wait", ms: 5600 },
        { type: "spotlight", target: sel("suggestionsReview") },
        { type: "wait", ms: 2000 },
        { type: "spotlight", target: null },
        { type: "click", target: sel("reviewAccept") },
        { type: "waitFor", target: REVIEW_AT(1) },
        { type: "wait", ms: 3200 },
        { type: "click", target: sel("reviewAccept") },
        { type: "waitFor", target: REVIEW_AT(2) },
      ],
    },
    {
      id: "rewrite",
      narration:
        "The third is a rewrite. The AI can reword a nearby sentence so everything flows, so you see the Original and the Rewritten side by side. I'd rather keep my wording, so I'll discard it. You decide on every change.",
      actions: [
        // "so you see the Original and the Rewritten" at 5.9 s, "so I'll discard it" at about 10.5 s.
        { type: "waitFor", target: REVIEW_AT(2) },
        { type: "wait", ms: 5600 },
        { type: "spotlight", target: sel("suggestionsReview") },
        { type: "wait", ms: 4300 },
        { type: "spotlight", target: null },
        { type: "click", target: sel("reviewDiscard") },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "score",
      narration: "Back in the report, the placeholder is filled, the answered suggestions are gone, and the score has gone up.",
      actions: [
        { type: "moveTo", target: sel("reportScore") },
        { type: "spotlight", target: sel("reportScore") },
        { type: "wait", ms: 4500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "dictate",
      narration:
        "The second way is dictation. Click where you want the text to go, then click Transcribe Audio in the toolbar. Pick your microphone and, if you want, a template.",
      actions: [
        // "Click where you want the text to go" at 2.4 s, "click Transcribe Audio" at about 4.2 s.
        { type: "scrollIntoView", target: LAST_PARAGRAPH },
        { type: "wait", ms: 1800 },
        { type: "click", target: LAST_PARAGRAPH, pointer: true },
        { type: "selectText", target: LAST_PARAGRAPH, collapse: "end" },
        { type: "wait", ms: 800 },
        { type: "click", target: sel("editorTranscribe") },
        { type: "waitFor", target: sel("dictationEnhanced") },
        { type: "wait", ms: 300 },
      ],
    },
    {
      id: "modes",
      narration:
        "Enhanced Dictation cleans up what you say in your report style, using your Report Preferences. Pure Dictation is a near-verbatim transcript that you edit yourself. When you finish, the text is inserted at your cursor.",
      actions: [
        // "Pure Dictation" at 5.6 s, "When you finish" at 10.8 s.
        { type: "moveTo", target: sel("dictationEnhanced") },
        { type: "spotlight", target: sel("dictationEnhanced") },
        { type: "wait", ms: 4900 },
        { type: "moveTo", target: sel("dictationPure") },
        { type: "spotlight", target: sel("dictationPure") },
        { type: "wait", ms: 4600 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "outro",
      narration:
        "So which one? Use Address Suggestions for facts that belong somewhere specific in the narrative. Use Transcribe Audio for a whole new paragraph, like a supplemental or a follow-up interview.",
      actions: [
        { type: "wait", ms: 2500 },
        { type: "moveTo", target: sel("editorTranscribe") },
        { type: "spotlight", target: sel("editorTranscribe") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
  ],
};

export default scene;
